<template>
  <v-card
    class="w-100 d-flex align-center justify-center"
  >
    <v-sheet
      v-if="!isForgotPassword"
      class="w-33 pt-3"
    >
      <v-row>
        <v-col cols="12" align="center">
          <h1 class="text-h5 font-weight-medium mb-0">
            Welcome back
          </h1>
        </v-col>
        <v-col cols="12">
          <v-tabs v-model="loginTab" grow @update:model-value="learnerStore.resetLearnerLogin()">
            <v-tab value="email" class="text-none">Staff</v-tab>
            <v-tab value="learner" class="text-none">Learner</v-tab>
          </v-tabs>
        </v-col>
        <v-col align="center">
          <div class="tab-overlay">

            <!-- ── Email tab ───────────────────────────────── -->
            <div :style="{ opacity: loginTab === 'email' ? 1 : 0 }" :inert="loginTab !== 'email'">
              <v-form
                class="mt-5 mb-5"
                @submit.prevent="onStaffLoginSubmit"
              >
                <v-row>
                  <v-col cols="12">
                    <!-- Plain native inputs, not v-text-field: Vuetify's floating-label
                         state doesn't reliably re-sync when Chrome autofills these fields
                         (confirmed — even a correctly-synced model value leaves the label
                         un-floated, overlapping the autofilled text). The floating label
                         below is pure CSS (:placeholder-shown + sibling selector), driven
                         directly by the input's live value — no JS/Vue state involved, so
                         there's nothing for autofill to desync it from. -->
                    <div class="plain-field" :class="{ 'plain-field--error': emailErrorVisible }">
                      <div class="plain-field-box">
                        <input
                          id="login-email"
                          v-model="loginData.email"
                          type="email"
                          autocomplete="email"
                          placeholder=" "
                          class="plain-field-input"
                          @blur="emailTouched = true"
                        >
                        <label for="login-email" class="plain-field-label">Enter email</label>
                        <button
                          v-if="loginData.email"
                          type="button"
                          class="plain-field-clear"
                          aria-label="Clear email"
                          @click="loginData.email = ''"
                        >
                          <v-icon icon="mdi-close-circle" size="18" />
                        </button>
                      </div>
                      <div v-if="emailErrorVisible" class="plain-field-error">{{ emailError }}</div>
                    </div>
                    <div class="plain-field mt-6" :class="{ 'plain-field--error': passwordErrorVisible }">
                      <div class="plain-field-box">
                        <input
                          id="login-password"
                          v-model="loginData.password"
                          type="password"
                          autocomplete="current-password"
                          placeholder=" "
                          class="plain-field-input"
                          @blur="passwordTouched = true"
                        >
                        <label for="login-password" class="plain-field-label">Enter password</label>
                        <button
                          v-if="loginData.password"
                          type="button"
                          class="plain-field-clear"
                          aria-label="Clear password"
                          @click="loginData.password = ''"
                        >
                          <v-icon icon="mdi-close-circle" size="18" />
                        </button>
                      </div>
                      <div v-if="passwordErrorVisible" class="plain-field-error">{{ passwordError }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" class="d-flex justify-space-between mt-n4 px-0">
                    <v-checkbox-btn v-model="rememberMe" label="Remember me for 30 days" class="ml-1" />
                    <v-btn class="text-primary text-none font-weight-medium pr-3" variant="text" @click.stop="toggleIsForgotPassword">
                      Forgot password
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="mt-2">
                    <v-btn
                      type="submit"
                      elevation="7"
                      rounded="lg"
                      class="bg-primary w-100 text-none"
                      variant="elevated"
                      :loading="getIsLoadingAuthentication && getProvider == 'password'"
                      :disabled="getErrorAuthentication != null"
                    >
                      Log In
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
              <v-divider gradient>
                <span class="font-weight-light text-no-wrap" :class="{ 'text-disabled': getErrorAuthentication != null }">OR</span>
              </v-divider>
              <v-row align="center" justify="center" class="mt-1">
                <v-col cols="6" class="d-flex flex-row-reverse">
                  <google-authentication action="login" />
                </v-col>
                <v-col cols="6" class="d-flex">
                  <v-btn
                    block
                    class="text-none font-weight-regular py-1"
                    variant="outlined"
                    size="large"
                    rounded="lg"
                    min-height="auto"
                    height="auto"
                    href="/.auth/logout?post_logout_redirect_uri=/.auth/login/aad?post_login_redirect_uri=/login_user/aad"
                    :loading="getIsLoadingAuthentication && getProvider == 'aad'"
                    :disabled="getErrorAuthentication != null"
                  >
                    <div class="d-flex align-center">
                      <v-img width="45" cover src="/microsoft_entral_id_logo.svg" alt="" />
                      <span class="text-wrap text-center">Continue with  {{ getAvailableProviders.aad }}</span>
                    </div>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- ── Learner tab ─────────────────────────────── -->
            <div :style="{ opacity: loginTab === 'learner' ? 1 : 0 }" :inert="loginTab !== 'learner'">
              <v-alert v-if="loginError" type="error" variant="tonal" density="compact" class="mb-4 mt-4" closable @click:close="learnerStore.loginError = null">
                {{ loginError }}
              </v-alert>
              <v-alert
                v-if="loginStep === 'password' && loginMustChangePassword"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-4"
                title="Your first password is your username"
              >
                Type <strong>{{ learnerUsername }}</strong> in the password field — the same as your username. After you log in, you will choose a new password.
              </v-alert>
              <v-alert
                v-if="loginStep === 'password' && showDaForgotHelp"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-4"
                title="Ask your instructor"
                closable
                @click:close="showDaForgotHelp = false"
              >
                Direct Access accounts cannot reset a password by email. Ask your instructor to reset it. They will set it back to your username so you can log in and choose a new one.
              </v-alert>

              <!-- Unified learner form — single DOM, visibility-only changes between steps -->
              <v-form class="mt-5 mb-5" @submit.prevent="handleLearnerSubmit">
                <v-row>
                  <v-col cols="12">
                    <!-- All three inputs stack in the same grid cell; only one is visible -->
                    <div class="learner-overlay">
                      <div :style="{ visibility: loginStep === 'username' ? 'visible' : 'hidden' }" :inert="loginStep !== 'username'">
                        <v-text-field ref="usernameFieldRef" v-model="learnerUsername" label="Username" variant="outlined" clearable autofocus autocomplete="username" />
                      </div>
                      <div :style="{ visibility: loginStep === 'challenge' ? 'visible' : 'hidden' }" :inert="loginStep !== 'challenge'">
                        <v-text-field ref="emailPrefixFieldRef" v-model="learnerEmailPrefix" label="Email" :suffix="learnerEmailDomain" variant="outlined" clearable />
                      </div>
                      <div :style="{ visibility: loginStep === 'otp' ? 'visible' : 'hidden' }" :inert="loginStep !== 'otp'">
                        <v-otp-input ref="otpInputRef" v-model="learnerOtpCode" length="6" variant="outlined" autocomplete="one-time-code" @finish="handleLearnerSubmit" />
                      </div>
                      <div :style="{ visibility: loginStep === 'password' ? 'visible' : 'hidden' }" :inert="loginStep !== 'password'">
                        <v-text-field
                          ref="passwordFieldRef"
                          v-model="learnerPassword"
                          :label="loginMustChangePassword ? 'Password (your username)' : 'Password'"
                          type="password"
                          variant="outlined"
                          clearable
                          autocomplete="current-password"
                          :hint="loginMustChangePassword ? `Type ${learnerUsername} — that is your first password` : 'Enter the password you chose'"
                          persistent-hint
                        />
                      </div>
                      <div :style="{ visibility: loginStep === 'setPassword' ? 'visible' : 'hidden' }" :inert="loginStep !== 'setPassword'">
                        <v-text-field ref="newPasswordFieldRef" v-model="learnerNewPassword" label="New password" type="password" variant="outlined" clearable autocomplete="new-password" />
                      </div>
                    </div>
                    <!-- Filler: ghost password field (sets height) + subtitle text overlay -->
                    <div class="learner-overlay mt-6">
                      <div style="visibility: hidden;" inert>
                        <v-text-field label="Enter password" type="password" variant="outlined" />
                      </div>
                      <div
                        :style="{ visibility: loginStep !== 'username' && loginStep !== 'setPassword' ? 'visible' : 'hidden' }"
                        :inert="loginStep === 'username' || loginStep === 'setPassword'"
                        class="d-flex align-center justify-center text-body-2 text-medium-emphasis text-center"
                      >
                        <span v-if="loginStep === 'challenge'">Fill in the missing part of your email address</span>
                        <span v-else-if="loginStep === 'otp'">Enter the 6-digit code sent to <strong class="ml-1">{{ loginContactHint }}</strong></span>
                        <span v-else-if="loginStep === 'password' && loginMustChangePassword">Type your username as the password, then you will set a new one</span>
                        <span v-else-if="loginStep === 'password'">Enter the password you chose. Forgot it? Ask your instructor.</span>
                      </div>
                      <div :style="{ visibility: loginStep === 'setPassword' ? 'visible' : 'hidden' }" :inert="loginStep !== 'setPassword'">
                        <v-text-field v-model="learnerConfirmPassword" label="Confirm new password" type="password" variant="outlined" clearable autocomplete="new-password" />
                      </div>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    class="d-flex justify-space-between mt-n4 px-0"
                    :style="{ visibility: loginStep === 'password' ? 'visible' : 'hidden' }"
                    :inert="loginStep !== 'password'"
                  >
                    <span />
                    <v-btn
                      type="button"
                      class="text-primary text-none font-weight-medium pr-3"
                      variant="text"
                      @click.stop="showDaForgotHelp = true"
                    >
                      Forgot password
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="mt-2">
                    <v-btn type="submit" block elevation="7" rounded="lg" class="bg-primary text-none" variant="elevated" :loading="loginLoading" :disabled="isLearnerSubmitDisabled">
                      {{ learnerSubmitLabel }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="mt-3" :style="{ visibility: loginStep === 'otp' ? 'visible' : 'hidden' }" :inert="loginStep !== 'otp'">
                    <div class="d-flex align-center justify-center ga-1">
                      <span class="text-body-2 text-medium-emphasis">Didn't receive it?</span>
                      <v-btn type="button" variant="text" color="primary" size="small" class="text-none px-1" :loading="loginResending" @click="learnerStore.resendLearnerOtp(learnerEmailPrefix)">
                        Resend
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" class="mt-1" :style="{ visibility: loginStep !== 'username' ? 'visible' : 'hidden' }" :inert="loginStep === 'username'">
                    <v-btn type="button" block variant="text" class="text-none text-medium-emphasis" @click="learnerStore.resetLearnerLogin()">
                      <v-icon start>mdi-chevron-left</v-icon>
                      {{ loginStep === 'challenge' || loginStep === 'password' || loginStep === 'setPassword' ? 'Back' : 'Start over' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>

              <!-- Ghost of Staff tab social section — keeps height identical across tabs -->
              <div style="visibility: hidden;" inert>
                <v-divider gradient>
                  <span class="font-weight-light text-no-wrap">OR</span>
                </v-divider>
                <v-row align="center" justify="center" class="mt-1">
                  <v-col cols="6" class="d-flex flex-row-reverse">
                    <google-authentication action="login" />
                  </v-col>
                  <v-col cols="6" class="d-flex">
                    <v-btn
                      block
                      class="text-none font-weight-regular py-1"
                      variant="outlined"
                      size="large"
                      rounded="lg"
                      min-height="auto"
                      height="auto"
                    >
                      <div class="d-flex align-center">
                        <v-img width="45" cover src="/microsoft_entral_id_logo.svg" alt="" />
                        <span class="text-wrap text-center">Continue with Microsoft</span>
                      </div>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </div>

          </div>
        </v-col>
      </v-row>
    </v-sheet>
    <v-sheet
      v-else
      class="w-33 pt-3"
    >
      <v-row v-if="forgotData.isSent == null && forgotData.isGet == null">
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Forgot your password?
          </h1>
        </v-col>
        <v-col
          cols="12"
          align="center"
          class="text-subtitle-2 font-weight-medium"
        >
          Enter your email so we can send you a password reset link.
          Learners who sign in with a username should ask their instructor instead.
        </v-col>
        <v-col
          align="center"
        >
          <v-form
            class="mt-5"
            fast-fail
            validate-on="submit lazy input"
            @submit.prevent="(event) => sendForgotPassword(event)"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="forgotData.email"
                  label="Enter email"
                  type="email"
                  variant="outlined"
                  :rules="rules.email.slice(0, -1)"
                  clearable
                />
              </v-col>
              <v-col
                cols="12"
                class="pt-0"
              >
                <v-btn
                  block
                  type="submit"
                  elevation="7"
                  class="bg-primary text-none rounded-lg"
                  variant="elevated"
                  :loading="getIsLoadingAuthentication"
                  :disabled="getErrorAuthentication != null"
                >
                  Send email
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row v-else-if="forgotData.isSent">
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Password reset
          </h1>
        </v-col>
        <v-col
          cols="12"
          align="center"
          class="text-subtitle-2 font-weight-medium"
        >
          We sent an e-mail to {{ forgotData.email }}
        </v-col>
        <v-col class="text-subtitle-2 font-weight-medium align-center d-flex flex-column mt-5">
          <v-row align="center">
            <span class="mr-2">Didn't receive the e-mail?</span>

            <v-btn
              class="text-primary text-none font-weight-medium px-2"
              variant="text"
              @click.stop="sendForgotPassword"
            >
              Click to resend
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-else-if="forgotData.isGet">
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Reset your password
          </h1>
        </v-col>
        <v-col
          align="center"
        >
          <v-form
            class="mt-5 mb-5"
            fast-fail
            validate-on="submit lazy input"
            @submit.prevent="(event) => changePassword(event)"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :model-value="forgotData?.get?.user?.email"
                  label="Email address"
                  type="email"
                  variant="outlined"
                  readonly
                />
                <v-text-field
                  v-model="forgotData.password"
                  label="New password"
                  type="password"
                  variant="outlined"
                  :rules="rules.password"
                  clearable
                />
                <v-text-field
                  v-model="forgotData.confirmPassword"
                  label="Confirm new password"
                  type="password"
                  variant="outlined"
                  :rules="rulesConfirmPassword"
                  :disabled="forgotData.password == null"
                  clearable
                />
              </v-col>
              <v-col
                cols="12"
                class="mt-2"
              >
                <v-btn
                  type="submit"
                  elevation="7"
                  rounded="lg"
                  class="bg-primary w-100 text-none"
                  variant="elevated"
                  :loading="getIsLoadingAuthentication"
                  :disabled="getErrorAuthentication != null"
                >
                  Reset my password
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row v-if="forgotData.isGet == null">
        <v-col
          cols="12"
          align="center"
        >
          <v-btn
            class="ml-1 text-caption text-primary"
            variant="text"
            density="comfortable"
            @click.stop="toggleIsForgotPassword"
          >
            <template #prepend>
              <v-icon>mdi-chevron-left</v-icon>
            </template>
            Back to log in
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
  </v-card>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import { useAuthentication } from '@/stores/auth'
import { useUser } from '@/stores/user'

const authenticationStore = useAuthentication()
const learnerStore = useUser()

const {
  loginData,
  forgotData,
  rules,
  rememberMe,
  isForgotPassword,
  getAvailableProviders,
  getIsLoadingAuthentication,
  getErrorAuthentication,
  getProvider
} = storeToRefs(authenticationStore)

const {
  loginStep,
  loginContactHint,
  loginLoading,
  loginError,
  loginResending,
  loginMustChangePassword
} = storeToRefs(learnerStore)

const {
  login,
  toggleIsForgotPassword,
  sendForgotPassword,
  changePassword,
  confirmPassword
} = authenticationStore

const loginTab          = ref('email')
const learnerUsername   = ref('')
const learnerEmailPrefix = ref('')
const learnerOtpCode    = ref('')
const learnerPassword   = ref('')
const learnerNewPassword = ref('')
const learnerConfirmPassword = ref('')

const usernameFieldRef   = ref(null)
const emailPrefixFieldRef = ref(null)
const otpInputRef        = ref(null)
const passwordFieldRef   = ref(null)
const newPasswordFieldRef = ref(null)
const showDaForgotHelp = ref(false)

const emailTouched = ref(false)
const passwordTouched = ref(false)
const staffLoginSubmitted = ref(false)

const emailError = computed(() => {
  for (const rule of rules.value.email.slice(0, -1)) {
    const result = rule(loginData.value.email)
    if (result !== true) return result
  }
  return null
})

const passwordError = computed(() => {
  for (const rule of rules.value.password) {
    const result = rule(loginData.value.password)
    if (result !== true) return result
  }
  return null
})

// An empty field's only error is "required" — don't nag about that until the
// user actually tries to submit. A non-empty but invalid value (bad format,
// weak password, ...) still shows as soon as it's been touched, same as before.
const emailErrorVisible = computed(() => {
  if (!emailError.value) return false
  if (!loginData.value.email) return staffLoginSubmitted.value
  return emailTouched.value || staffLoginSubmitted.value
})

const passwordErrorVisible = computed(() => {
  if (!passwordError.value) return false
  if (!loginData.value.password) return staffLoginSubmitted.value
  return passwordTouched.value || staffLoginSubmitted.value
})

function onStaffLoginSubmit(event) {
  staffLoginSubmitted.value = true
  // v-form no longer wraps any :rules-bearing fields, so it can't gate this
  // submit on validity anymore — check manually before calling login(), which
  // otherwise calls .trim() on loginData.email/.password and would throw if
  // either is still null (e.g. submitting without ever touching a field).
  if (emailError.value || passwordError.value) return
  login('password', event)
}

const learnerSubmitLabel = computed(() =>
  ({ username: 'Continue', challenge: 'Verify', otp: 'Confirm', password: 'Log In', setPassword: 'Save password' })[loginStep.value] ?? 'Continue'
)

const isLearnerSubmitDisabled = computed(() => {
  if (loginStep.value === 'username') return !learnerUsername.value
  if (loginStep.value === 'challenge') return !learnerEmailPrefix.value
  if (loginStep.value === 'otp') return (learnerOtpCode.value?.length ?? 0) < 6
  if (loginStep.value === 'password') return !learnerPassword.value
  if (loginStep.value === 'setPassword') {
    return !learnerNewPassword.value ||
      learnerNewPassword.value.length < 8 ||
      learnerNewPassword.value !== learnerConfirmPassword.value ||
      learnerNewPassword.value.toLowerCase() === learnerUsername.value.toLowerCase()
  }
  return false
})

function handleLearnerSubmit() {
  if (loginStep.value === 'username') learnerStore.startLearnerLogin(learnerUsername.value)
  else if (loginStep.value === 'challenge') learnerStore.submitLearnerChallenge(learnerEmailPrefix.value)
  else if (loginStep.value === 'otp') learnerStore.submitLearnerOtp(learnerOtpCode.value)
  else if (loginStep.value === 'password') learnerStore.submitLearnerPassword(learnerPassword.value)
  else if (loginStep.value === 'setPassword') learnerStore.submitLearnerSetPassword(learnerNewPassword.value)
}

const rulesConfirmPassword = ref([])

watch(loginTab, (tab) => {
  learnerUsername.value    = ''
  learnerEmailPrefix.value = ''
  learnerOtpCode.value     = ''
  learnerPassword.value    = ''
  learnerNewPassword.value = ''
  learnerConfirmPassword.value = ''
  showDaForgotHelp.value = false
  if (tab === 'learner') nextTick(() => usernameFieldRef.value?.focus())
})

const learnerEmailDomain = computed(() => {
  if (!loginContactHint.value) return '@…'
  const atIdx = loginContactHint.value.indexOf('@')
  return atIdx !== -1 ? loginContactHint.value.slice(atIdx) : '@…'
})

watch(loginStep, (step) => {
  learnerOtpCode.value = ''
  learnerPassword.value = ''
  learnerNewPassword.value = ''
  learnerConfirmPassword.value = ''
  showDaForgotHelp.value = false
  if (step === 'challenge' && loginContactHint.value) {
    learnerEmailPrefix.value = loginContactHint.value.charAt(0)
  }
  nextTick(() => {
    if (step === 'username') usernameFieldRef.value?.focus()
    else if (step === 'challenge') emailPrefixFieldRef.value?.focus()
    else if (step === 'otp') otpInputRef.value?.focus()
    else if (step === 'password') passwordFieldRef.value?.focus()
    else if (step === 'setPassword') newPasswordFieldRef.value?.focus()
  })
})

onBeforeMount(async () => {
  rulesConfirmPassword.value = [
    ...rules.value.password,
    (confirmValue) => confirmPassword(confirmValue, forgotData.value.confirmPassword)
  ]
})
</script>

<style scoped>
.custom-btn {
  min-height: auto;
  height: auto;
}

/* Outer tab switcher — opacity:0 default can't be overridden by descendants' visibility */
.tab-overlay {
  display: grid;
}
.tab-overlay > * {
  grid-area: 1 / 1;
  opacity: 0;
}

/* Inner step/input switchers — visibility toggled per child via inline style */
.learner-overlay {
  display: grid;
}
.learner-overlay > * {
  grid-area: 1 / 1;
}

/* Plain (non-Vuetify) styled field, used for the Staff email/password inputs — see
   the comment above them for why. The floating label is pure CSS: the input needs
   placeholder=" " so :placeholder-shown only matches when truly empty, and the
   label must come *after* the input in the DOM so the `~` sibling selector below
   can react to the input's own live state (:focus / :not(:placeholder-shown)) —
   no JS, so autofill (or anything else that sets .value) can't desync it. */
.plain-field-box {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity, 0.38));
  border-radius: 4px;
  padding: 0 12px;
  background: transparent;
}
.plain-field-box:focus-within {
  border-color: rgb(var(--v-theme-primary));
  border-width: 2px;
  padding: 0 11px;
}
.plain-field--error .plain-field-box {
  border-color: rgb(var(--v-theme-error));
}
.plain-field-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 56px;
  background: transparent;
  border: none;
  outline: none;
  color: rgb(var(--v-theme-on-surface));
  font-size: 1rem;
  font-family: inherit;
}
.plain-field-label {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  line-height: 1;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity, 0.7));
  padding: 0 4px;
  pointer-events: none;
  transition: top 0.15s ease, font-size 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}
.plain-field-input:focus ~ .plain-field-label,
.plain-field-input:not(:placeholder-shown) ~ .plain-field-label,
.plain-field-input:-webkit-autofill ~ .plain-field-label {
  top: 0;
  font-size: 0.75rem;
  color: rgb(var(--v-theme-primary));
  /* Only the floated (on-the-border) position needs the opaque "notch" cut —
     giving the resting position an opaque background too would let it visually
     cover the start of the value if the label is ever unfloated while a value
     is genuinely present (belt-and-suspenders against autofill sources that
     don't trigger :-webkit-autofill at all, e.g. some password-manager
     extensions). */
  background: rgb(var(--v-theme-surface));
}
.plain-field--error .plain-field-label {
  color: rgb(var(--v-theme-error));
}
/* Higher specificity than the :focus/:not(:placeholder-shown)/:-webkit-autofill
   rule above, so error red wins over the floated-label primary color when both
   states apply. */
.plain-field--error .plain-field-input:focus ~ .plain-field-label,
.plain-field--error .plain-field-input:not(:placeholder-shown) ~ .plain-field-label,
.plain-field--error .plain-field-input:-webkit-autofill ~ .plain-field-label {
  color: rgb(var(--v-theme-error));
}
/* Chrome's default autofill background clashes with the dark theme — force it
   transparent instead. No floating-label state involved here, so this is the
   whole fix for autofill on this field. */
.plain-field-input:-webkit-autofill,
.plain-field-input:-webkit-autofill:hover,
.plain-field-input:-webkit-autofill:focus {
  -webkit-text-fill-color: rgb(var(--v-theme-on-surface));
  -webkit-box-shadow: 0 0 0 1000px transparent inset;
  transition: background-color 9999s ease-in-out 0s;
}
.plain-field-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
.plain-field-error {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-error));
  margin-top: 4px;
  padding-left: 12px;
}
</style>
